/**
 * CommunityInitWizard - 5-Step Community Initialization Wizard
 *
 * Steps:
 *   1. Community Info - Name, description, location, energy market type
 *   2. Location & Settings - Grid connection, tariff structure, currency
 *   3. Buildings & Assets - Add buildings with their building-level assets (all types)
 *   4. Community Assets - Add community-wide shared assets (PV, Battery only)
 *   5. Review & Create - Summary and confirmation
 *
 * Routes: /communities/new
 * On completion, navigates to /communities/:id/dashboard
 */

import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import {
  Container, Row, Col, Card, Form, Button, Badge, Alert, Table,
  InputGroup, OverlayTrigger, Tooltip, Spinner
} from 'react-bootstrap'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { countries } from '../data/dummyData'
import { useAuth } from '../context/AuthContext'
import { communityKeys } from '../hooks/useCommunities'
import { createCommunity, updateCommunity, getCommunity } from '../api/communities'
import { createBuilding, deleteBuilding } from '../api/buildings'
import { createBuildingAsset, deleteBuildingAsset } from '../api/buildingAssets'
import { createAsset, deleteAsset } from '../api/assets'

const MARKET_TYPES = [
  { value: 'retail', i18nKey: 'community_wizard.market_retail' },
  { value: 'wholesale', i18nKey: 'community_wizard.market_wholesale' },
  { value: 'p2p', i18nKey: 'community_wizard.market_p2p' },
  { value: 'net_metering', i18nKey: 'community_wizard.market_net_metering' },
]

const BUILDING_TYPES = [
  { value: 'residential', i18nKey: 'community_wizard.building_residential', defaultConsumption: 3500 },
  { value: 'commercial', i18nKey: 'community_wizard.building_commercial', defaultConsumption: 25000 },
  { value: 'industrial', i18nKey: 'community_wizard.building_industrial', defaultConsumption: 150000 },
  { value: 'public', i18nKey: 'community_wizard.building_public', defaultConsumption: 45000 },
]

const ASSET_TYPES = {
  pv: {
    i18nKey: 'community_wizard.asset_pv',
    icon: '\u2600\uFE0F',
    defaultSpecs: { capacity_kwp: 10, efficiency_pct: 18, tilt_deg: 30, azimuth_deg: 180 },
  },
  battery: {
    i18nKey: 'community_wizard.asset_battery',
    icon: '\uD83D\uDD0B',
    defaultSpecs: { capacity_kwh: 10, charge_rate_kw: 5, discharge_rate_kw: 5, efficiency_pct: 90 },
  },
  ev_charger: {
    i18nKey: 'community_wizard.asset_ev_charger',
    icon: '\uD83D\uDD0C',
    defaultSpecs: { max_power_kw: 7.4, num_evs: 1, controllable: 'yes' },
  },
  heat_pump: {
    i18nKey: 'community_wizard.asset_heat_pump',
    icon: '\uD83C\uDF21\uFE0F',
    defaultSpecs: { rated_power_kw: 5, cop: 3.5, controllable: 'yes', mode: 'heating' },
  },
  hvac: {
    i18nKey: 'community_wizard.asset_hvac',
    icon: '\u2744\uFE0F',
    defaultSpecs: { rated_power_kw: 8, cop_heating: 3.5, cop_cooling: 3.0, controllable: 'yes', mode: 'both' },
  },
}

// Community-level: only generation/storage, no flexible loads
const COMMUNITY_ASSET_TYPES = ['pv', 'battery']

// Currency conversion rates (to EUR)
const CURRENCY_RATES = {
  EUR: 1,
  CZK: 24.39, // 1 EUR = ~24.39 CZK
}

/** Convert a EUR price to the target currency */
function eurTo(currency, eurValue) {
  return +(eurValue * (CURRENCY_RATES[currency] || 1)).toFixed(3)
}

/** Convert a local currency price back to EUR */
function toEur(currency, localValue) {
  return +(localValue / (CURRENCY_RATES[currency] || 1)).toFixed(6)
}

/** Get the currency symbol */
function currencySymbol(code) {
  return code === 'CZK' ? 'K\u010D' : '\u20AC'
}

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/** Render asset spec summary text */
function assetSpecSummary(asset) {
  const s = asset.specs
  switch (asset.asset_type) {
    case 'pv': return `${s.capacity_kwp} kWp, ${s.efficiency_pct}% eff.`
    case 'battery': return `${s.capacity_kwh} kWh, ${s.charge_rate_kw}/${s.discharge_rate_kw} kW`
    case 'ev_charger': return `${s.max_power_kw} kW, ${s.num_evs} EV(s)`
    case 'heat_pump': return `${s.rated_power_kw} kW, COP ${s.cop}`
    case 'hvac': return `${s.rated_power_kw} kW, ${s.mode}`
    default: return ''
  }
}

/** Render asset spec input fields based on type */
function AssetSpecFields({ assetType, specs, onUpdate }) {
  const { t } = useTranslation()
  switch (assetType) {
    case 'pv':
      return (
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.spec_capacity_kwp')}</Form.Label>
              <Form.Control type="number" step="0.1" min="0" value={specs.capacity_kwp}
                onChange={e => onUpdate('capacity_kwp', parseFloat(e.target.value))} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.spec_efficiency')}</Form.Label>
              <Form.Control type="number" step="0.1" min="0" max="100" value={specs.efficiency_pct}
                onChange={e => onUpdate('efficiency_pct', parseFloat(e.target.value))} />
            </Form.Group>
          </Col>
        </Row>
      )
    case 'battery':
      return (
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.spec_capacity_kwh')}</Form.Label>
              <Form.Control type="number" step="0.1" min="0" value={specs.capacity_kwh}
                onChange={e => onUpdate('capacity_kwh', parseFloat(e.target.value))} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.spec_charge_rate')}</Form.Label>
              <Form.Control type="number" step="0.1" min="0" value={specs.charge_rate_kw}
                onChange={e => onUpdate('charge_rate_kw', parseFloat(e.target.value))} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.spec_discharge_rate')}</Form.Label>
              <Form.Control type="number" step="0.1" min="0" value={specs.discharge_rate_kw}
                onChange={e => onUpdate('discharge_rate_kw', parseFloat(e.target.value))} />
            </Form.Group>
          </Col>
        </Row>
      )
    case 'ev_charger':
      return (
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.spec_max_power')}</Form.Label>
              <Form.Control type="number" step="0.1" min="0" value={specs.max_power_kw}
                onChange={e => onUpdate('max_power_kw', parseFloat(e.target.value))} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.spec_num_evs')}</Form.Label>
              <Form.Control type="number" min="1" value={specs.num_evs}
                onChange={e => onUpdate('num_evs', parseInt(e.target.value))} />
            </Form.Group>
          </Col>
        </Row>
      )
    case 'heat_pump':
      return (
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.spec_rated_power')}</Form.Label>
              <Form.Control type="number" step="0.1" min="0" value={specs.rated_power_kw}
                onChange={e => onUpdate('rated_power_kw', parseFloat(e.target.value))} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.spec_cop')}</Form.Label>
              <Form.Control type="number" step="0.1" min="1" max="10" value={specs.cop}
                onChange={e => onUpdate('cop', parseFloat(e.target.value))} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.spec_mode')}</Form.Label>
              <Form.Select value={specs.mode} onChange={e => onUpdate('mode', e.target.value)}>
                <option value="heating">{t('community_wizard.mode_heating')}</option>
                <option value="cooling">{t('community_wizard.mode_cooling')}</option>
                <option value="both">{t('community_wizard.mode_both')}</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      )
    case 'hvac':
      return (
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.spec_rated_power')}</Form.Label>
              <Form.Control type="number" step="0.1" min="0" value={specs.rated_power_kw}
                onChange={e => onUpdate('rated_power_kw', parseFloat(e.target.value))} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.spec_cop_heating')}</Form.Label>
              <Form.Control type="number" step="0.1" min="1" max="10" value={specs.cop_heating}
                onChange={e => onUpdate('cop_heating', parseFloat(e.target.value))} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.spec_cop_cooling')}</Form.Label>
              <Form.Control type="number" step="0.1" min="1" max="10" value={specs.cop_cooling}
                onChange={e => onUpdate('cop_cooling', parseFloat(e.target.value))} />
            </Form.Group>
          </Col>
        </Row>
      )
    default:
      return null
  }
}

/** Inline asset list display */
function AssetList({ assets, onRemove }) {
  const { t } = useTranslation()
  if (!assets || assets.length === 0) return null
  return (
    <div className="d-flex flex-column gap-2 mt-2">
      {assets.map((asset, idx) => {
        const info = ASSET_TYPES[asset.asset_type]
        if (!info) return null
        return (
          <div key={idx} className="d-flex align-items-center justify-content-between p-2"
            style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-neutral-50)' }}>
            <div className="d-flex align-items-center gap-3">
              <span style={{ fontSize: '1.25rem' }}>{info.icon}</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{t(info.i18nKey)}</div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
                  {assetSpecSummary(asset)}
                </div>
              </div>
            </div>
            <Button variant="outline-danger" size="sm" onClick={() => onRemove(idx)}>{t('community_wizard.btn_remove')}</Button>
          </div>
        )
      })}
    </div>
  )
}

function CommunityInitWizard() {
  const navigate = useNavigate()
  const { id: editId } = useParams()  // present when editing existing community
  const isEditMode = !!editId
  const queryClient = useQueryClient()
  const { user, isCountryManager } = useAuth()
  const { t } = useTranslation()
  const [step, setStep] = useState(1)
  const [isLoadingEdit, setIsLoadingEdit] = useState(!!editId)
  // Track original DB IDs for edit mode (to handle deletions/updates)
  const [originalData, setOriginalData] = useState(null)

  const [formData, setFormData] = useState({
    name: '', description: '', country: '', location: '', energyMarketType: 'retail',
    timezone: 'Europe/Athens', currency: 'EUR', gridConnectionType: 'lv',
    importTariff: 0.155, exportTariff: 0.071, gridCharges: 0.035,
    buildings: [],
    buildingAssets: {},   // { buildingIndex: [...assets] }
    communityAssets: [],  // [...assets] - PV/Battery only
  })

  // Load existing community data in edit mode
  useEffect(() => {
    if (!editId) return
    let cancelled = false
    ;(async () => {
      try {
        const community = await getCommunity(editId)
        if (cancelled) return
        const meta = community.metadata || {}
        const buildings = community.buildings || []
        const communityAssets = community.community_assets || []

        // Map DB asset types back to wizard format
        const assetTypeReverseMap = { solar_pv_farm: 'pv', ground_mounted_pv: 'pv', rooftop_pv: 'pv', community_battery: 'battery', wind_turbine: 'wind' }
        const buildingAssetTypeMap = { rooftop_pv: 'pv', chp: 'heat_pump' }  // DB types → wizard types

        // Build form buildings and their assets
        const formBuildings = []
        const formBuildingAssets = {}
        const originalBuildings = []

        for (let idx = 0; idx < buildings.length; idx++) {
          const b = buildings[idx]
          formBuildings.push({
            building_id: b.building_id || b.metadata?.name || `building-${idx + 1}`,
            type: b.building_type || 'residential',
            floor_area_m2: b.floor_area_m2 || 100,
            num_units: b.metadata?.num_units || 1,
            annual_consumption_kwh: b.metadata?.annual_consumption_kwh || 5000,
          })
          originalBuildings.push({ dbId: b.id, building_id: b.building_id })

          // Map building assets (convert DB types to wizard types)
          const bAssets = b.building_assets || []
          if (bAssets.length > 0) {
            formBuildingAssets[idx] = bAssets
              .map(a => {
                const wizardType = buildingAssetTypeMap[a.asset_type] || a.asset_type
                if (!ASSET_TYPES[wizardType]) return null  // skip unknown types
                return { asset_type: wizardType, specs: a.specs || {}, _dbId: a.id }
              })
              .filter(Boolean)
          }
        }

        // Map community assets (filter out types not supported by wizard)
        const formCommunityAssets = communityAssets
          .map(a => {
            const wizardType = assetTypeReverseMap[a.asset_type] || a.asset_type
            if (!ASSET_TYPES[wizardType]) return null
            return { asset_type: wizardType, specs: a.specs || {}, _dbId: a.id }
          })
          .filter(Boolean)

        setFormData({
          name: community.name || '',
          description: meta.description || '',
          country: meta.country || '',
          location: meta.location || '',
          energyMarketType: meta.energy_market_type || 'retail',
          timezone: community.timezone || 'Europe/Athens',
          currency: community.currency || 'EUR',
          gridConnectionType: meta.grid_connection_type || 'lv',
          importTariff: meta.import_tariff || 0,
          exportTariff: meta.export_tariff || 0,
          gridCharges: meta.grid_charges || 0,
          buildings: formBuildings,
          buildingAssets: formBuildingAssets,
          communityAssets: formCommunityAssets,
        })

        setOriginalData({
          buildings: originalBuildings,
          buildingAssets: buildings.map(b => (b.building_assets || []).map(a => a.id)),
          communityAssets: communityAssets.map(a => a.id),
        })
      } catch (err) {
        console.error('Failed to load community for editing:', err)
      } finally {
        if (!cancelled) setIsLoadingEdit(false)
      }
    })()
    return () => { cancelled = true }
  }, [editId])

  // For country_manager: pre-fill and lock country based on user profile (only in create mode)
  useEffect(() => {
    if (isEditMode) return  // don't override loaded data
    if (isCountryManager && user?.country_name) {
      const c = countries.find(x => x.value === user.country_name)
      if (c) {
        const newCurrency = c.currency || user.currency || 'EUR'
        const rate = CURRENCY_RATES[newCurrency] || 1
        setFormData(prev => ({
          ...prev,
          country: c.value,
          timezone: c.timezone || user.timezone || prev.timezone,
          currency: newCurrency,
          importTariff: +(0.155 * rate).toFixed(3),
          exportTariff: +(0.071 * rate).toFixed(3),
          gridCharges: +(0.035 * rate).toFixed(3),
        }))
      }
    }
  }, [isCountryManager, user, isEditMode]) // eslint-disable-line react-hooks/exhaustive-deps

  const [errors, setErrors] = useState({})

  // Building form
  const [buildingForm, setBuildingForm] = useState({
    building_id: '', type: 'residential', floor_area_m2: 120, num_units: 1, annual_consumption_kwh: 3500,
  })

  // Building-level asset form (shown in Step 3)
  const [buildingAssetForm, setBuildingAssetForm] = useState({
    selectedBuildingIndex: 0,
    asset_type: 'pv',
    specs: { ...ASSET_TYPES.pv.defaultSpecs },
  })

  // Community-level asset form (shown in Step 4)
  const [communityAssetForm, setCommunityAssetForm] = useState({
    asset_type: 'pv',
    specs: { ...ASSET_TYPES.pv.defaultSpecs },
  })

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (field === 'country' && value) {
      const c = countries.find(x => x.value === value)
      if (c) {
        const newCurrency = c.currency || 'EUR'
        setFormData(prev => {
          // Convert tariff values from current currency to EUR, then to new currency
          const oldRate = CURRENCY_RATES[prev.currency] || 1
          const newRate = CURRENCY_RATES[newCurrency] || 1
          const factor = newRate / oldRate
          return {
            ...prev,
            timezone: c.timezone,
            currency: newCurrency,
            importTariff: +(prev.importTariff * factor).toFixed(3),
            exportTariff: +(prev.exportTariff * factor).toFixed(3),
            gridCharges: +(prev.gridCharges * factor).toFixed(3),
          }
        })
      }
    }
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }))
  }

  const validateStep = () => {
    const e = {}
    if (step === 1) {
      if (!formData.name?.trim()) e.name = t('community_wizard.error_name_required')
      if (!formData.country) e.country = t('community_wizard.error_country_required')
      if (!formData.location?.trim()) e.location = t('community_wizard.error_location_required')
    }
    if (step === 2) {
      if (formData.importTariff <= 0) e.importTariff = t('community_wizard.error_import_tariff_positive')
      if (formData.exportTariff < 0) e.exportTariff = t('community_wizard.error_export_tariff_nonneg')
    }
    setErrors(e)
    return Object.keys(e).length === 0
  }

  // Building CRUD
  const addBuilding = () => {
    if (!buildingForm.building_id?.trim()) { alert(t('community_wizard.alert_building_name')); return }
    setFormData(prev => ({ ...prev, buildings: [...prev.buildings, { ...buildingForm }] }))
    setBuildingForm({ building_id: '', type: 'residential', floor_area_m2: 120, num_units: 1, annual_consumption_kwh: 3500 })
  }

  const removeBuilding = (index) => {
    setFormData(prev => ({
      ...prev,
      buildings: prev.buildings.filter((_, i) => i !== index),
      buildingAssets: Object.fromEntries(
        Object.entries(prev.buildingAssets).filter(([k]) => parseInt(k) !== index)
      ),
    }))
  }

  // Building-level asset CRUD
  const addBuildingAsset = () => {
    const idx = buildingAssetForm.selectedBuildingIndex
    setFormData(prev => ({
      ...prev,
      buildingAssets: {
        ...prev.buildingAssets,
        [idx]: [...(prev.buildingAssets[idx] || []), { asset_type: buildingAssetForm.asset_type, specs: { ...buildingAssetForm.specs } }],
      },
    }))
    setBuildingAssetForm(prev => ({ ...prev, asset_type: 'pv', specs: { ...ASSET_TYPES.pv.defaultSpecs } }))
  }

  const removeBuildingAsset = (bIdx, aIdx) => {
    setFormData(prev => ({
      ...prev,
      buildingAssets: { ...prev.buildingAssets, [bIdx]: prev.buildingAssets[bIdx].filter((_, i) => i !== aIdx) },
    }))
  }

  // Community-level asset CRUD
  const addCommunityAsset = () => {
    setFormData(prev => ({
      ...prev,
      communityAssets: [...prev.communityAssets, { asset_type: communityAssetForm.asset_type, specs: { ...communityAssetForm.specs } }],
    }))
    setCommunityAssetForm({ asset_type: 'pv', specs: { ...ASSET_TYPES.pv.defaultSpecs } })
  }

  const removeCommunityAsset = (idx) => {
    setFormData(prev => ({ ...prev, communityAssets: prev.communityAssets.filter((_, i) => i !== idx) }))
  }

  const nextStep = () => { if (validateStep()) setStep(s => Math.min(s + 1, 5)) }
  const prevStep = () => setStep(s => Math.max(s - 1, 1))

  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)

  const completeWizard = async () => {
    if (!validateStep()) return
    setIsSaving(true)
    setSaveError(null)

    try {
      if (isEditMode) {
        // --- UPDATE existing community ---
        const communityId = editId

        // 1. Update community metadata
        await updateCommunity(communityId, {
          name: formData.name,
          timezone: formData.timezone || 'Europe/Prague',
          currency: formData.currency || 'EUR',
          metadata: {
            description: formData.description,
            country: formData.country,
            location: formData.location,
            energy_market_type: formData.energyMarketType,
            grid_connection_type: formData.gridConnectionType,
            import_tariff: formData.importTariff,
            export_tariff: formData.exportTariff,
            grid_charges: formData.gridCharges,
            status: 'active',
          },
        })

        // 2. Delete old buildings (cascade-deletes their assets) then recreate
        if (originalData?.buildings) {
          for (const ob of originalData.buildings) {
            try { await deleteBuilding(communityId, ob.dbId) } catch (e) { console.warn('Delete building failed:', e.message) }
          }
        }

        // 3. Recreate buildings with current form state
        for (let idx = 0; idx < formData.buildings.length; idx++) {
          const b = formData.buildings[idx]
          const building = await createBuilding(communityId, {
            building_id: b.building_id || `building-${idx + 1}`,
            building_type: b.type || b.building_type || 'residential',
            floor_area_m2: b.floor_area_m2 || 100,
            energy_class: b.energy_class || 'C',
            metadata: {
              name: b.name || b.building_id,
              num_units: b.num_units || 1,
              annual_consumption_kwh: b.annual_consumption_kwh || 5000,
            },
          })
          const buildingAssets = formData.buildingAssets[idx] || []
          for (const a of buildingAssets) {
            try {
              await createBuildingAsset(communityId, building.id, {
                asset_type: a.asset_type,
                specs: a.specs || {},
              })
            } catch (err) { console.warn(`Failed to create building asset:`, err.message) }
          }
        }

        // 4. Delete old community assets and recreate
        if (originalData?.communityAssets) {
          for (const assetId of originalData.communityAssets) {
            try { await deleteAsset(communityId, assetId) } catch (e) { console.warn('Delete asset failed:', e.message) }
          }
        }
        for (const a of formData.communityAssets) {
          const assetTypeMap = { pv: 'solar_pv_farm', battery: 'community_battery', wind: 'wind_turbine' }
          try {
            await createAsset(communityId, {
              name: ASSET_TYPES[a.asset_type]?.label || a.asset_type,
              asset_type: assetTypeMap[a.asset_type] || a.asset_type,
              specs: a.specs || {},
            })
          } catch (err) { console.warn(`Failed to create community asset:`, err.message) }
        }

        // 5. Invalidate cache and navigate
        queryClient.invalidateQueries({ queryKey: communityKeys.all })
        queryClient.invalidateQueries({ queryKey: communityKeys.detail(communityId) })
        navigate(`/communities/${communityId}/dashboard`)

      } else {
        // --- CREATE new community ---
        const community = await createCommunity({
          name: formData.name,
          timezone: formData.timezone || 'Europe/Prague',
          currency: formData.currency || 'EUR',
          metadata: {
            description: formData.description,
            country: formData.country,
            location: formData.location,
            energy_market_type: formData.energyMarketType,
            grid_connection_type: formData.gridConnectionType,
            import_tariff: formData.importTariff,
            export_tariff: formData.exportTariff,
            grid_charges: formData.gridCharges,
            status: 'setup',
          },
        })
        const communityId = community.id

        // Create buildings and their assets
        for (let idx = 0; idx < formData.buildings.length; idx++) {
          const b = formData.buildings[idx]
          const building = await createBuilding(communityId, {
            building_id: b.building_id || `building-${idx + 1}`,
            building_type: b.type || b.building_type || 'residential',
            floor_area_m2: b.floor_area_m2 || 100,
            energy_class: b.energy_class || 'C',
            metadata: {
              name: b.name || b.building_id,
              num_units: b.num_units || 1,
              annual_consumption_kwh: b.annual_consumption_kwh || 5000,
            },
          })
          const buildingAssets = formData.buildingAssets[idx] || []
          for (const a of buildingAssets) {
            try {
              await createBuildingAsset(communityId, building.id, {
                asset_type: a.asset_type,
                specs: a.specs || {},
              })
            } catch (err) { console.warn(`Failed to create building asset:`, err.message) }
          }
        }

        // Create community-level assets
        for (const a of formData.communityAssets) {
          const assetTypeMap = { pv: 'solar_pv_farm', battery: 'community_battery', wind: 'wind_turbine' }
          try {
            await createAsset(communityId, {
              name: ASSET_TYPES[a.asset_type]?.label || a.asset_type,
              asset_type: assetTypeMap[a.asset_type] || a.asset_type,
              specs: a.specs || {},
            })
          } catch (err) { console.warn(`Failed to create community asset:`, err.message) }
        }

        // Mark community as active
        await updateCommunity(communityId, {
          metadata: {
            ...community.metadata,
            description: formData.description,
            country: formData.country,
            location: formData.location,
            energy_market_type: formData.energyMarketType,
            grid_connection_type: formData.gridConnectionType,
            import_tariff: formData.importTariff,
            export_tariff: formData.exportTariff,
            grid_charges: formData.gridCharges,
            status: 'active',
          },
        })

        queryClient.invalidateQueries({ queryKey: communityKeys.all })
        navigate(`/communities/${communityId}/dashboard`)
      }
    } catch (err) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} community:`, err)
      setSaveError(err.message || (isEditMode ? t('community_wizard.save_error_update') : t('community_wizard.save_error_create')))
    } finally {
      setIsSaving(false)
    }
  }

  const updateBuildingForm = (field, value) => {
    setBuildingForm(prev => ({ ...prev, [field]: value }))
    if (field === 'type') {
      const bt = BUILDING_TYPES.find(t => t.value === value)
      if (bt) setBuildingForm(prev => ({ ...prev, annual_consumption_kwh: bt.defaultConsumption * prev.num_units }))
    }
    if (field === 'num_units') {
      const bt = BUILDING_TYPES.find(t => t.value === buildingForm.type)
      if (bt) setBuildingForm(prev => ({ ...prev, annual_consumption_kwh: bt.defaultConsumption * value }))
    }
  }

  const steps = [
    { number: 1, label: t('community_wizard.step1_label') },
    { number: 2, label: t('community_wizard.step2_label') },
    { number: 3, label: t('community_wizard.step3_label') },
    { number: 4, label: t('community_wizard.step4_label') },
    { number: 5, label: isEditMode ? t('community_wizard.step5_label_edit') : t('community_wizard.step5_label_create') },
  ]

  if (isLoadingEdit) {
    return (
      <Container style={{ maxWidth: '900px', paddingTop: 'var(--space-6)' }}>
        <div className="text-center py-5">
          <Spinner animation="border" style={{ color: 'var(--color-primary)' }} />
          <p className="mt-3" style={{ color: 'var(--color-neutral-600)' }}>{t('community_wizard.loading_community')}</p>
        </div>
      </Container>
    )
  }

  return (
    <Container style={{ maxWidth: '900px', paddingTop: 'var(--space-6)', paddingBottom: 'var(--space-8)' }}>
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center gap-3 mb-3">
            <Link to={isEditMode ? `/communities/${editId}/dashboard` : '/communities'} style={{ color: 'var(--color-neutral-600)', textDecoration: 'none', fontSize: 'var(--font-size-sm)' }}>
              &larr; {isEditMode ? t('community_wizard.back_to_dashboard') : t('community_wizard.back_to_communities')}
            </Link>
          </div>
          <h1 style={{ color: 'var(--color-primary-dark)', fontSize: 'var(--font-size-2xl)', marginBottom: 'var(--space-2)' }}>
            {isEditMode ? t('community_wizard.edit_title') : t('community_wizard.create_title')}
          </h1>
          <p style={{ color: 'var(--color-neutral-600)', fontSize: 'var(--font-size-md)' }}>
            {isEditMode ? t('community_wizard.edit_subtitle') : t('community_wizard.create_subtitle')}
          </p>
        </Col>
      </Row>

      {/* Progress Indicator */}
      <div className="wizard-steps mb-5">
        {steps.map((s, index) => (
          <React.Fragment key={s.number}>
            <div className={`wizard-step ${step === s.number ? 'active' : ''} ${step > s.number ? 'completed' : ''}`}>
              <div className="wizard-step-number">{step > s.number ? '\u2713' : s.number}</div>
              <span className="wizard-step-label">{s.label}</span>
            </div>
            {index < steps.length - 1 && (
              <div className={`wizard-step-connector ${step > s.number ? 'completed' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      <Card className="shadow-sm" style={{ borderRadius: 'var(--radius-lg)', border: 'none' }}>
        <Card.Body style={{ padding: 'var(--space-8)' }}>

          {/* ========== STEP 1: COMMUNITY INFO ========== */}
          {step === 1 && (
            <div>
              <h4 style={{ color: 'var(--color-primary-dark)', marginBottom: 'var(--space-2)', fontWeight: 600 }}>
                {t('community_wizard.step1_title')}
              </h4>
              <p style={{ color: 'var(--color-neutral-600)', marginBottom: 'var(--space-6)' }}>
                {t('community_wizard.step1_subtitle')}
              </p>

              <Form.Group className="mb-4">
                <Form.Label style={{ fontWeight: 600 }}>
                  {t('community_wizard.field_name')} <span style={{ color: 'var(--color-danger)' }}>*</span>
                </Form.Label>
                <Form.Control type="text" placeholder={t('community_wizard.field_name_placeholder')} size="lg"
                  value={formData.name} onChange={e => updateField('name', e.target.value)} isInvalid={!!errors.name} />
                <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                <Form.Text className="text-muted">{t('community_wizard.field_name_hint')}</Form.Text>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.field_description')}</Form.Label>
                <Form.Control as="textarea" rows={3} placeholder={t('community_wizard.field_description_placeholder')}
                  value={formData.description} onChange={e => updateField('description', e.target.value)} />
                <Form.Text className="text-muted">{t('community_wizard.field_description_hint')}</Form.Text>
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label style={{ fontWeight: 600 }}>
                      {t('community_wizard.field_country')} <span style={{ color: 'var(--color-danger)' }}>*</span>
                    </Form.Label>
                    <Form.Select
                      value={formData.country}
                      onChange={e => updateField('country', e.target.value)}
                      isInvalid={!!errors.country}
                      disabled={isCountryManager}
                      style={isCountryManager ? { backgroundColor: 'var(--color-neutral-100)' } : {}}
                    >
                      <option value="">{t('community_wizard.field_country_select')}</option>
                      {countries.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </Form.Select>
                    {isCountryManager && (
                      <Form.Text className="text-muted">{t('community_wizard.field_country_locked')}</Form.Text>
                    )}
                    <Form.Control.Feedback type="invalid">{errors.country}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label style={{ fontWeight: 600 }}>
                      {t('community_wizard.field_location')} <span style={{ color: 'var(--color-danger)' }}>*</span>
                    </Form.Label>
                    <Form.Control type="text" placeholder={t('community_wizard.field_location_placeholder')}
                      value={formData.location} onChange={e => updateField('location', e.target.value)} isInvalid={!!errors.location} />
                    <Form.Control.Feedback type="invalid">{errors.location}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-4">
                <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.field_market_type')}</Form.Label>
                <Form.Select value={formData.energyMarketType} onChange={e => updateField('energyMarketType', e.target.value)}>
                  {MARKET_TYPES.map(m => <option key={m.value} value={m.value}>{t(m.i18nKey)}</option>)}
                </Form.Select>
                <Form.Text className="text-muted">{t('community_wizard.field_market_type_hint')}</Form.Text>
              </Form.Group>
            </div>
          )}

          {/* ========== STEP 2: LOCATION & SETTINGS ========== */}
          {step === 2 && (
            <div>
              <h4 style={{ color: 'var(--color-primary-dark)', marginBottom: 'var(--space-2)', fontWeight: 600 }}>
                {t('community_wizard.step2_title')}
              </h4>
              <p style={{ color: 'var(--color-neutral-600)', marginBottom: 'var(--space-6)' }}>
                {t('community_wizard.step2_subtitle')}
              </p>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.field_timezone')}</Form.Label>
                    <Form.Control type="text" value={formData.timezone} readOnly disabled
                      style={{ backgroundColor: 'var(--color-neutral-100)' }} />
                    <Form.Text className="text-muted">{t('community_wizard.field_timezone_hint')}</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.field_currency')}</Form.Label>
                    <Form.Control type="text"
                      value={formData.currency === 'CZK' ? 'CZK - Czech Koruna (K\u010D)' : 'EUR - Euro (\u20AC)'}
                      readOnly disabled style={{ backgroundColor: 'var(--color-neutral-100)' }} />
                    <Form.Text className="text-muted">{t('community_wizard.field_currency_hint')}</Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-4">
                <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.field_grid_connection')}</Form.Label>
                <Form.Select value={formData.gridConnectionType} onChange={e => updateField('gridConnectionType', e.target.value)}>
                  <option value="lv">{t('community_wizard.grid_lv')}</option>
                  <option value="mv">{t('community_wizard.grid_mv')}</option>
                  <option value="hv">{t('community_wizard.grid_hv')}</option>
                </Form.Select>
              </Form.Group>

              <h6 style={{ fontWeight: 600, marginTop: 'var(--space-6)', marginBottom: 'var(--space-4)' }}>
                {t('community_wizard.tariff_structure')}
              </h6>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-4">
                    <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.field_import_tariff')} <span style={{ color: 'var(--color-danger)' }}>*</span></Form.Label>
                    <InputGroup>
                      <Form.Control type="number" step="0.001" min="0" value={formData.importTariff}
                        onChange={e => updateField('importTariff', parseFloat(e.target.value))} isInvalid={!!errors.importTariff} />
                      <InputGroup.Text>{formData.currency}/kWh</InputGroup.Text>
                    </InputGroup>
                    <Form.Text className="text-muted">{t('community_wizard.field_import_tariff_hint')}</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-4">
                    <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.field_export_tariff')} <span style={{ color: 'var(--color-danger)' }}>*</span></Form.Label>
                    <InputGroup>
                      <Form.Control type="number" step="0.001" min="0" value={formData.exportTariff}
                        onChange={e => updateField('exportTariff', parseFloat(e.target.value))} isInvalid={!!errors.exportTariff} />
                      <InputGroup.Text>{formData.currency}/kWh</InputGroup.Text>
                    </InputGroup>
                    <Form.Text className="text-muted">{t('community_wizard.field_export_tariff_hint')}</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-4">
                    <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.field_grid_charges')}</Form.Label>
                    <InputGroup>
                      <Form.Control type="number" step="0.001" min="0" value={formData.gridCharges}
                        onChange={e => updateField('gridCharges', parseFloat(e.target.value))} />
                      <InputGroup.Text>{formData.currency}/kWh</InputGroup.Text>
                    </InputGroup>
                    <Form.Text className="text-muted">{t('community_wizard.field_grid_charges_hint')}</Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Alert variant="info" className="mb-0">
                <div className="d-flex">
                  <div className="me-3" style={{ fontSize: '1.25rem' }}>&#x1F4A1;</div>
                  <div>
                    <strong>{t('community_wizard.tariff_tip_prefix')}</strong> {t('community_wizard.tariff_tip_text')}
                  </div>
                </div>
              </Alert>
            </div>
          )}

          {/* ========== STEP 3: BUILDINGS & ASSETS ========== */}
          {step === 3 && (
            <div>
              <h4 style={{ color: 'var(--color-primary-dark)', marginBottom: 'var(--space-2)', fontWeight: 600 }}>
                {t('community_wizard.step3_title')}
              </h4>
              <p style={{ color: 'var(--color-neutral-600)', marginBottom: 'var(--space-6)' }}>
                {t('community_wizard.step3_subtitle')}
              </p>

              {/* Existing Buildings with their assets */}
              {formData.buildings.length > 0 && formData.buildings.map((building, bIdx) => {
                const bAssets = formData.buildingAssets[bIdx] || []
                const buildingTypeRow = BUILDING_TYPES.find(x => x.value === building.type)
                return (
                  <Card key={bIdx} className="mb-3" style={{ border: '1px solid var(--color-neutral-200)' }}>
                    <Card.Body>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="d-flex align-items-center gap-2">
                          <h6 style={{ fontWeight: 600, margin: 0 }}>{building.building_id}</h6>
                          <Badge bg="secondary">{buildingTypeRow ? t(buildingTypeRow.i18nKey) : building.type}</Badge>
                        </div>
                        <Button variant="outline-danger" size="sm" onClick={() => removeBuilding(bIdx)}>{t('community_wizard.btn_remove')}</Button>
                      </div>
                      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-neutral-600)' }}>
                        {building.floor_area_m2} m&sup2; &bull; {building.num_units} &bull; {building.annual_consumption_kwh.toLocaleString()} kWh/yr
                      </div>
                      <AssetList assets={bAssets} onRemove={(aIdx) => removeBuildingAsset(bIdx, aIdx)} />
                    </Card.Body>
                  </Card>
                )
              })}

              {/* Add Building Form */}
              <Card className="mb-4" style={{ border: '2px dashed var(--color-neutral-300)', backgroundColor: 'var(--color-neutral-50)' }}>
                <Card.Body>
                  <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-4)' }}>{t('community_wizard.add_building')}</h6>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.building_name')}</Form.Label>
                        <Form.Control type="text" placeholder={t('community_wizard.building_name_placeholder')}
                          value={buildingForm.building_id} onChange={e => updateBuildingForm('building_id', e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.building_type')}</Form.Label>
                        <Form.Select value={buildingForm.type} onChange={e => updateBuildingForm('type', e.target.value)}>
                          {BUILDING_TYPES.map(bt => <option key={bt.value} value={bt.value}>{t(bt.i18nKey)}</option>)}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.floor_area')}</Form.Label>
                        <Form.Control type="number" min="1" value={buildingForm.floor_area_m2}
                          onChange={e => updateBuildingForm('floor_area_m2', parseInt(e.target.value))} />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.num_units')}</Form.Label>
                        <Form.Control type="number" min="1" value={buildingForm.num_units}
                          onChange={e => updateBuildingForm('num_units', parseInt(e.target.value))} />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ fontWeight: 600 }}>
                          {t('community_wizard.consumption')}
                          <OverlayTrigger placement="top" overlay={<Tooltip>{t('community_wizard.consumption_tooltip')}</Tooltip>}>
                            <span style={{ marginLeft: '0.25rem', cursor: 'help' }}>\u24D8</span>
                          </OverlayTrigger>
                        </Form.Label>
                        <Form.Control type="number" min="0" value={buildingForm.annual_consumption_kwh}
                          onChange={e => updateBuildingForm('annual_consumption_kwh', parseInt(e.target.value))} />
                      </Form.Group>
                    </Col>
                  </Row>
                  <div className="d-flex justify-content-end">
                    <Button style={{ backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)' }} onClick={addBuilding}>
                      {t('community_wizard.add_building_btn')}
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              {/* Add Building Asset Form - shown only when buildings exist */}
              {formData.buildings.length > 0 && (
                <Card style={{ border: '2px dashed var(--color-accent)', backgroundColor: '#FFF8F0' }}>
                  <Card.Body>
                    <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-4)', color: 'var(--color-accent-dark)' }}>
                      {t('community_wizard.add_asset_to_building')}
                    </h6>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.field_building')}</Form.Label>
                          <Form.Select value={buildingAssetForm.selectedBuildingIndex}
                            onChange={e => setBuildingAssetForm(prev => ({ ...prev, selectedBuildingIndex: parseInt(e.target.value) }))}>
                            {formData.buildings.map((b, idx) => <option key={idx} value={idx}>{b.building_id}</option>)}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.field_asset_type')}</Form.Label>
                          <Form.Select value={buildingAssetForm.asset_type}
                            onChange={e => {
                              const newType = e.target.value
                              setBuildingAssetForm(prev => ({ ...prev, asset_type: newType, specs: { ...ASSET_TYPES[newType].defaultSpecs } }))
                            }}>
                            {Object.entries(ASSET_TYPES).map(([k, v]) => (
                              <option key={k} value={k}>{v.icon} {t(v.i18nKey)}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <AssetSpecFields assetType={buildingAssetForm.asset_type} specs={buildingAssetForm.specs}
                      onUpdate={(field, val) => setBuildingAssetForm(prev => ({ ...prev, specs: { ...prev.specs, [field]: val } }))} />
                    <div className="d-flex justify-content-end">
                      <Button style={{ backgroundColor: 'var(--color-accent)', borderColor: 'var(--color-accent)' }} onClick={addBuildingAsset}>
                        {t('community_wizard.add_asset_btn')}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              )}

              {formData.buildings.length === 0 && (
                <Alert variant="info" className="mt-4 mb-0">
                  <strong>{t('community_wizard.buildings_optional_prefix')}</strong> {t('community_wizard.buildings_optional_text')}
                </Alert>
              )}
            </div>
          )}

          {/* ========== STEP 4: COMMUNITY ASSETS ========== */}
          {step === 4 && (
            <div>
              <h4 style={{ color: 'var(--color-primary-dark)', marginBottom: 'var(--space-2)', fontWeight: 600 }}>
                {t('community_wizard.step4_title')}
              </h4>
              <p style={{ color: 'var(--color-neutral-600)', marginBottom: 'var(--space-6)' }}>
                {t('community_wizard.step4_subtitle')}
              </p>

              {/* Existing community assets list */}
              {formData.communityAssets.length > 0 && (
                <Card className="mb-4" style={{ border: '1px solid var(--color-primary)', borderLeft: '4px solid var(--color-primary)' }}>
                  <Card.Body>
                    <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)', color: 'var(--color-primary-dark)' }}>
                      {t('community_wizard.added_assets_count', { count: formData.communityAssets.length })}
                    </h6>
                    <AssetList assets={formData.communityAssets} onRemove={removeCommunityAsset} />
                  </Card.Body>
                </Card>
              )}

              {/* Add community asset form - PV and Battery only */}
              <Card style={{ border: '2px dashed var(--color-neutral-300)', backgroundColor: 'var(--color-neutral-50)' }}>
                <Card.Body>
                  <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-4)' }}>{t('community_wizard.add_community_asset')}</h6>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: 600 }}>{t('community_wizard.field_asset_type')}</Form.Label>
                    <Form.Select value={communityAssetForm.asset_type}
                      onChange={e => {
                        const newType = e.target.value
                        setCommunityAssetForm({ asset_type: newType, specs: { ...ASSET_TYPES[newType].defaultSpecs } })
                      }}>
                      {COMMUNITY_ASSET_TYPES.map(k => (
                        <option key={k} value={k}>{ASSET_TYPES[k].icon} {t(ASSET_TYPES[k].i18nKey)}</option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      {t('community_wizard.community_asset_hint')}
                    </Form.Text>
                  </Form.Group>

                  <AssetSpecFields assetType={communityAssetForm.asset_type} specs={communityAssetForm.specs}
                    onUpdate={(field, val) => setCommunityAssetForm(prev => ({ ...prev, specs: { ...prev.specs, [field]: val } }))} />

                  <div className="d-flex justify-content-end">
                    <Button style={{ backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)' }} onClick={addCommunityAsset}>
                      {t('community_wizard.add_community_asset_btn')}
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              <Alert variant="info" className="mt-4 mb-0">
                <strong>{t('community_wizard.buildings_optional_prefix')}</strong> {t('community_wizard.community_assets_optional_text')}
              </Alert>
            </div>
          )}

          {/* ========== STEP 5: REVIEW & CREATE ========== */}
          {step === 5 && (
            <div>
              <h4 style={{ color: 'var(--color-primary-dark)', marginBottom: 'var(--space-2)', fontWeight: 600 }}>
                {isEditMode ? t('community_wizard.step5_label_edit') : t('community_wizard.step5_label_create')}
              </h4>
              <p style={{ color: 'var(--color-neutral-600)', marginBottom: 'var(--space-6)' }}>
                {t('community_wizard.step5_subtitle')}
              </p>

              {/* Community Info */}
              <Card className="mb-4" style={{ border: '1px solid var(--color-primary-light)', backgroundColor: 'var(--color-primary-pale)' }}>
                <Card.Body>
                  <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>{t('community_wizard.review_info')}</h6>
                  <Row style={{ fontSize: 'var(--font-size-sm)' }}>
                    <Col md={6}>
                      <div className="mb-2"><strong>{t('community_wizard.review_name')}</strong> {formData.name}</div>
                      <div className="mb-2"><strong>{t('community_wizard.review_location')}</strong> {formData.location}, {formData.country}</div>
                      <div className="mb-2"><strong>{t('community_wizard.review_timezone')}</strong> {formData.timezone}</div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-2"><strong>{t('community_wizard.review_market_type')}</strong> {(() => { const m = MARKET_TYPES.find(x => x.value === formData.energyMarketType); return m ? t(m.i18nKey) : formData.energyMarketType })()}</div>
                      <div className="mb-2"><strong>{t('community_wizard.review_grid_connection')}</strong> {formData.gridConnectionType.toUpperCase()}</div>
                      <div className="mb-2"><strong>{t('community_wizard.review_currency')}</strong> {formData.currency}</div>
                    </Col>
                  </Row>
                  {formData.description && (
                    <div className="mt-3" style={{ fontSize: 'var(--font-size-sm)' }}>
                      <strong>{t('community_wizard.review_description')}</strong> {formData.description}
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* Tariff */}
              <Card className="mb-4" style={{ border: '1px solid var(--color-neutral-200)' }}>
                <Card.Body>
                  <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>{t('community_wizard.tariff_structure')}</h6>
                  <Row style={{ fontSize: 'var(--font-size-sm)' }}>
                    <Col xs={4}>
                      <div className="mb-1" style={{ color: 'var(--color-neutral-600)' }}>{t('community_wizard.review_import_tariff')}</div>
                      <div style={{ fontWeight: 600 }}>{formData.importTariff.toFixed(3)} {formData.currency}/kWh</div>
                    </Col>
                    <Col xs={4}>
                      <div className="mb-1" style={{ color: 'var(--color-neutral-600)' }}>{t('community_wizard.review_export_tariff')}</div>
                      <div style={{ fontWeight: 600 }}>{formData.exportTariff.toFixed(3)} {formData.currency}/kWh</div>
                    </Col>
                    <Col xs={4}>
                      <div className="mb-1" style={{ color: 'var(--color-neutral-600)' }}>{t('community_wizard.review_grid_charges')}</div>
                      <div style={{ fontWeight: 600 }}>{formData.gridCharges.toFixed(3)} {formData.currency}/kWh</div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Buildings & their assets */}
              <Card className="mb-4" style={{ border: '1px solid var(--color-neutral-200)' }}>
                <Card.Body>
                  <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>
                    {t('community_wizard.review_buildings', { count: formData.buildings.length })}
                  </h6>
                  {formData.buildings.length === 0 ? (
                    <div style={{ color: 'var(--color-neutral-500)', fontSize: 'var(--font-size-sm)' }}>
                      {t('community_wizard.review_no_buildings')}
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-2">
                      {formData.buildings.map((b, idx) => {
                        const btRow = BUILDING_TYPES.find(x => x.value === b.type)
                        return (
                          <div key={idx} style={{ padding: 'var(--space-3)', border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)' }}>
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <strong>{b.building_id}</strong>
                              <Badge bg="secondary">{btRow ? t(btRow.i18nKey) : b.type}</Badge>
                            </div>
                            <div style={{ color: 'var(--color-neutral-600)' }}>
                              {b.floor_area_m2} m&sup2; &bull; {b.num_units} &bull; {b.annual_consumption_kwh.toLocaleString()} kWh/yr
                            </div>
                            {formData.buildingAssets[idx]?.length > 0 && (
                              <div className="mt-2" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
                                <strong>{t('community_wizard.review_assets_label')}</strong> {formData.buildingAssets[idx].map(a => `${ASSET_TYPES[a.asset_type].icon} ${t(ASSET_TYPES[a.asset_type].i18nKey)}`).join(', ')}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* Community Assets */}
              <Card className="mb-4" style={{ border: '1px solid var(--color-neutral-200)' }}>
                <Card.Body>
                  <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>
                    {t('community_wizard.review_community_assets', { count: formData.communityAssets.length })}
                  </h6>
                  {formData.communityAssets.length === 0 ? (
                    <div style={{ color: 'var(--color-neutral-500)', fontSize: 'var(--font-size-sm)' }}>
                      {t('community_wizard.review_no_community_assets')}
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-2">
                      {formData.communityAssets.map((a, idx) => (
                        <div key={idx} className="d-flex align-items-center gap-3"
                          style={{ padding: 'var(--space-2)', border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)' }}>
                          <span style={{ fontSize: '1.25rem' }}>{ASSET_TYPES[a.asset_type].icon}</span>
                          <div>
                            <div style={{ fontWeight: 600 }}>{t(ASSET_TYPES[a.asset_type].i18nKey)}</div>
                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>{assetSpecSummary(a)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>

              <Alert variant="success" className="mb-0">
                <div className="d-flex">
                  <div className="me-3" style={{ fontSize: '1.5rem' }}>{'\u2713'}</div>
                  <div>
                    <strong>{isEditMode ? t('community_wizard.ready_to_save') : t('community_wizard.ready_to_create')}</strong>
                    <p className="mb-0" style={{ fontSize: 'var(--font-size-sm)' }}>
                      {isEditMode ? t('community_wizard.ready_save_text') : t('community_wizard.ready_create_text')}
                    </p>
                  </div>
                </div>
              </Alert>
            </div>
          )}

          {saveError && (
            <Alert variant="danger" className="mt-3" dismissible onClose={() => setSaveError(null)}>
              <strong>{t('community_wizard.error_prefix')}</strong> {saveError}
            </Alert>
          )}

          {/* Navigation */}
          <div className="d-flex justify-content-between mt-5">
            <div>
              {step > 1 && (
                <Button variant="outline-secondary" onClick={prevStep}
                  style={{ paddingLeft: 'var(--space-6)', paddingRight: 'var(--space-6)' }}>
                  {t('community_wizard.nav_back')}
                </Button>
              )}
            </div>
            <div className="d-flex gap-2">
              {step < 5 ? (
                <Button onClick={nextStep}
                  style={{ backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)', fontWeight: 600, paddingLeft: 'var(--space-8)', paddingRight: 'var(--space-8)' }}>
                  {t('community_wizard.nav_next')}
                </Button>
              ) : (
                <Button onClick={completeWizard} disabled={isSaving}
                  style={{ backgroundColor: 'var(--color-accent)', borderColor: 'var(--color-accent)', fontWeight: 600, padding: 'var(--space-3) var(--space-8)', fontSize: 'var(--font-size-md)' }}>
                  {isSaving ? <><Spinner size="sm" className="me-2" />{isEditMode ? t('community_wizard.nav_saving') : t('community_wizard.nav_creating')}</> : (isEditMode ? t('community_wizard.nav_save') : t('community_wizard.nav_create'))}
                </Button>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Help Tip */}
      <Card className="mt-4 shadow-sm" style={{ border: '1px solid var(--color-neutral-200)' }}>
        <Card.Body>
          <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>
            &#x1F4A1; {t('community_wizard.help_title')}
          </h6>
          <ul className="mb-0" style={{ fontSize: 'var(--font-size-sm)' }}>
            <li className="mb-2">{t('community_wizard.help_item_1')}</li>
            <li className="mb-2">{t('community_wizard.help_item_2')}</li>
            <li className="mb-2">{t('community_wizard.help_item_3')}</li>
            <li className="mb-2">{t('community_wizard.help_item_4')}</li>
          </ul>
        </Card.Body>
      </Card>
    </Container>
  )
}

export default CommunityInitWizard
